import React from 'react';

import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/ui/buttons/Button';
import ErrorList from '@/components/ui/forms/ErrorList';
import Field from '@/components/ui/forms/Field';
import FormGroup from '@/components/ui/forms/FormGroup';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import Modal from '@/components/ui/modals/Modal';
import useFetch from '@/hooks/common/useFetch';
import { type ModalInnerProps } from '@/hooks/common/useModal';
import useReactHookForm from '@/hooks/common/useReactHookForm';

import CSSModule from './ProfileEditPasswordConfirmModal.module.scss';

interface Props extends ModalInnerProps {
  redirectPath: string;
}

interface FormValues {
  password: string;
}

interface ErrorResponse {
  message: string;
  code?: string;
}

const ProfileEditPasswordConfirmModal: React.FC<Props> = ({
  isOpen,
  toggle,
  redirectPath,
}) => {
  const navigate = useNavigate();

  const { fetch, isFetching, error } = useFetch<null, ErrorResponse>(
    'POST',
    '/api/school/parents/password/verify',
  );

  const validationSchema = Yup.object().shape({
    password: Yup.string().required('パスワードは必須です。'),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid, errorMessages },
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: { password: '' },
  });

  const onSubmit = async (formValues: FormValues) => {
    const { status } = await fetch({ body: { password: formValues.password } });

    if (status === 200) {
      toggle();
      navigate(redirectPath, {
        state: {
          authenticated: true,
        },
      });
    }
  };

  const formErrors = [
    ...(errorMessages.password || []),
    ...(error ? [error.message] : []),
  ];

  const inputDisabled = isSubmitting || isFetching;
  const buttonDisabled = !isDirty || !isValid || isSubmitting || isFetching;

  return (
    <Modal isOpen={isOpen} toggle={toggle} title="パスワードの入力">
      <form
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(onSubmit)}
        className={CSSModule.ProfileEditPasswordConfirmModal}
      >
        <p className={CSSModule.ProfileEditPasswordConfirmModal__Description}>
          編集をするにあたり、現在のパスワードを入力してください
        </p>
        <FormGroup>
          <Field>
            <PasswordInput
              {...registerWithInnerRef('password')}
              disabled={inputDisabled}
              autoComplete="new-password"
              placeholder="現在のパスワードを入力"
            />
            <ErrorList errors={formErrors} />
          </Field>
        </FormGroup>
        <a
          href="https://studysapuri-school.jp/studentfaq/s/article/000001852"
          target="_blank"
          rel="noopener noreferrer"
          className={
            CSSModule.ProfileEditPasswordConfirmModal__ForgotPasswordLink
          }
        >
          パスワードを忘れた方はこちら
        </a>
        <div className={CSSModule.ProfileEditPasswordConfirmModal__Buttons}>
          <Button
            color="outline"
            size="s"
            className={CSSModule.ProfileEditPasswordConfirmModal__Button}
            onClick={toggle}
          >
            キャンセル
          </Button>
          <Button
            type="submit"
            color="primary"
            size="s"
            disabled={buttonDisabled}
            className={CSSModule.ProfileEditPasswordConfirmModal__Button}
          >
            次へ
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ProfileEditPasswordConfirmModal;
